import React from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import type { RoomUsageAnalyticsItem } from "@/types/roomUsage";

interface RoomsUsagePerformanceCardProps {
  items: RoomUsageAnalyticsItem[];
  isLoading?: boolean;
}

export function RoomsUsagePerformanceCard({
  items,
  isLoading = false,
}: RoomsUsagePerformanceCardProps) {
  const { t } = useTranslation();

  const chartConfig = React.useMemo(
    () =>
      ({
        bookedNights: {
          label: t("reservation.roomUsage.bookedNights"),
          color: "#C9A962",
        },
      }) satisfies ChartConfig,
    [t],
  );

  const chartData = React.useMemo(
    () =>
      items
        .slice()
        .sort((a, b) => b.bookedNights - a.bookedNights)
        .slice(0, 18)
        .map((item) => ({
          entityName: item.entityName,
          bookedNights: item.bookedNights,
          staysCount: item.staysCount,
        })),
    [items],
  );
  const maxBookedNights = React.useMemo(
    () => Math.max(...chartData.map((item) => item.bookedNights), 0),
    [chartData],
  );
  const yAxisTicks = React.useMemo(() => {
    if (maxBookedNights <= 0) {
      return [0];
    }

    return Array.from({ length: maxBookedNights + 1 }, (_, index) => index);
  }, [maxBookedNights]);

  if (!chartData.length) {
    return (
      <div className="mt-6 rounded-2xl border border-border bg-background p-6">
        <div className="py-12 text-center text-sm text-muted-foreground">
          {isLoading
            ? t("reservation.roomUsage.loading")
            : t("reservation.roomUsage.analyticsEmpty")}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 w-full rounded-2xl border border-border bg-background px-4 py-3">
      <ChartContainer
        config={chartConfig}
        className="h-[185px] w-full !aspect-auto"
      >
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} stroke="#F4F4F5" strokeWidth={1} />
          <XAxis
            dataKey="entityName"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fill: "#A3A3A3", fontSize: 12 }}
          />
          <YAxis
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            ticks={yAxisTicks}
            width={28}
            tick={{ fill: "#A3A3A3", fontSize: 12 }}
            domain={[0, maxBookedNights || 1]}
          />
          <Tooltip
            cursor={{ fill: "rgba(244, 244, 245, 0.55)" }}
            content={
              <ChartTooltipContent
                formatter={(value) => (
                  <>
                    <span>{t("reservation.roomUsage.bookedNights")}</span>
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {Number(value).toLocaleString()}
                    </span>
                  </>
                )}
                labelFormatter={(_, payload) => {
                  const row = payload?.[0]?.payload as
                    | { entityName?: string; staysCount?: number }
                    | undefined;

                  if (!row?.entityName) {
                    return "";
                  }

                  return (
                    <>
                      <span>{row.entityName}</span>
                      <span className="ml-3 text-muted-foreground">
                        {t("reservation.roomUsage.staysCount")}:{" "}
                        {row.staysCount ?? 0}
                      </span>
                    </>
                  );
                }}
              />
            }
          />
          <Bar
            dataKey="bookedNights"
            fill="var(--color-bookedNights)"
            radius={[12, 12, 12, 12]}
            maxBarSize={34}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}
